/* ────────────────────────────────────────────────────────────────────────────
 * Ведомость материалов по РД (модуль 1.2), библиотека РД объектов и сверка с
 * заявками (модуль 1.4) — типы и хелперы.
 *
 * Библиотека РД — документы, которые загрузил и разобрал ПТО. Снабжение видит
 * её по всем объектам (только чтение) и строит по выбранным документам
 * ведомость: перечень материалов, свод арматуры по классу и диаметру, коды 1С.
 * Служебные поля строк (вид строки, масса, источники, итог сверки) backend
 * кладёт в `raw_data` позиции — разбираем их здесь безопасно.
 * ──────────────────────────────────────────────────────────────────────────── */

import type { BadgeTone, MtoMaterialPosition, MtoRun } from './types';

/** Объект (проект ПТО) библиотеки РД со сводкой по документам. */
export interface RdLibraryProject {
  id: string;
  name: string;
  code: string | null;
  status: string;
  /** Документов с действующей редакцией. */
  documents: number;
  indexed: number;
  processing: number;
  failed: number;
}

/** Статус разбора документа в ПТО. */
export type RdIngestStatus = 'pending' | 'parsing' | 'indexed' | 'failed';

/** Документ РД объекта с действующей редакцией. */
export interface RdLibraryDocument {
  document_id: string;
  code: string;
  mark: string | null;
  title: string;
  discipline: string | null;
  revision_id: string;
  revision_no: number;
  rev_label: string | null;
  ingest_status: RdIngestStatus | string;
  total_pages: number | null;
  source_filename: string;
  uploaded_at: string;
  error_message: string | null;
  /** Позиций, извлечённых ПТО. */
  positions: number;
}

export function rdIngestLabel(status: string): string {
  switch (status) {
    case 'indexed':
      return 'разобран';
    case 'pending':
      return 'в очереди ПТО';
    case 'parsing':
      return 'разбирается';
    case 'failed':
      return 'ошибка разбора';
    default:
      return status;
  }
}

export function rdIngestTone(status: string): BadgeTone {
  switch (status) {
    case 'indexed':
      return 'success';
    case 'pending':
    case 'parsing':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Марка комплекта документа: из реестра, иначе хвост шифра после последней
 *  точки («…-02.02.071-Б.КЖ1» → «КЖ1»). */
export function documentMark(d: Pick<RdLibraryDocument, 'mark' | 'code'>): string {
  const mark = d.mark?.trim();
  if (mark) return mark;
  const tail = d.code.split('.').pop() ?? '';
  return tail.split('-')[0]?.trim() || '—';
}

/** Группа марки для фильтра: только буквы («КЖ1» → «КЖ», «ЭС2» → «ЭС»). */
export function markGroup(mark: string): string {
  const letters = mark.replace(/[^A-Za-zА-Яа-яЁё]/g, '').toUpperCase();
  return letters || '—';
}

/** Документ, попавший (или не попавший) в ведомость. */
export interface VedomostDocInfo {
  label: string;
  title: string | null;
  revision_id: string | null;
  filename: string | null;
  /** `library` | `library_file` | `tables` | `spreadsheet` | `ocr`. */
  origin: string;
  /** `ok` | `empty` | `processing` | `failed`. */
  status: string;
  rows: number;
  note: string | null;
}

export interface VedomostTotals {
  materials: number;
  rebar_rows: number;
  table_rows: number;
  review: number;
  total_weight_kg: number | null;
  rebar_total_kg: number | null;
  /** Строк «на 1 …» по разбору ПТО и сколько из них с кратностью по РД. */
  per_unit_rows?: number;
  per_unit_found?: number;
  /** Итоги с учётом кратности «на 1 …» по РД (на проверку). Нет — кратность
   *  не найдена ни у одной строки. */
  total_weight_mult_kg?: number;
  rebar_total_mult_kg?: number;
  /** Строк, у которых кратность указал снабженец. */
  per_unit_manual?: number;
  /** Строк работ из ВОР (не материалы). */
  vor_work_rows?: number;
}

export interface ReconcileTotals {
  need: number;
  requests: number;
  ok: number;
  not_ordered: number;
  under: number;
  over: number;
  without_need: number;
}

/** Параметры и итоги прогона (`MtoRun.raw_data`). */
export interface MtoRunRaw {
  source?: 'library' | 'upload';
  project_id?: string;
  project_name?: string;
  revision_ids?: string[];
  documents?: VedomostDocInfo[];
  notes?: string[];
  totals?: Partial<VedomostTotals & ReconcileTotals>;
  /** Сверка: ведомость, из которой взята потребность. */
  rd_run_id?: string;
  rd_run_summary?: string;
  /** Ведомость сформирована заново из этой. */
  reformed_from?: string;
  /** Сколько решений по строкам перенесено из прежней ведомости. */
  carried_decisions?: number;
}

/** Что изменилось у документа ведомости после её формирования. */
export type VedomostChange =
  | 'changed'
  | 'ready'
  | 'reparsing'
  | 'parse_failed'
  | 'new_revision'
  | 'gone';

export interface VedomostDocChange {
  label: string;
  revision_id: string;
  change: VedomostChange;
}

/** Актуальность ведомости относительно текущего разбора ПТО. */
export interface VedomostFreshness {
  /** Проверка применима: завершённая ведомость с документами библиотеки РД. */
  applicable: boolean;
  stale: boolean;
  changes: VedomostDocChange[];
  /** Можно сформировать заново из библиотеки (ведомость из своих файлов — нет). */
  can_reform: boolean;
  /** Ведомость уже сформирована заново — последняя новая. */
  reformed_to: string | null;
}

export function vedomostChangeLabel(change: VedomostChange | string): string {
  switch (change) {
    case 'changed':
      return 'ПТО изменил разбор документа';
    case 'ready':
      return 'документ разобран в ПТО — его можно включить в ведомость';
    case 'reparsing':
      return 'ПТО заново разбирает документ';
    case 'parse_failed':
      return 'повторный разбор в ПТО завершился ошибкой';
    case 'new_revision':
      return 'в библиотеке новая редакция документа';
    case 'gone':
      return 'документ удалён из библиотеки РД';
    default:
      return change;
  }
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function runRaw(run: Pick<MtoRun, 'raw_data'>): MtoRunRaw {
  return isObj(run.raw_data) ? (run.raw_data as MtoRunRaw) : {};
}

/** Итог сверки строки. */
export type ReconcileStatus =
  | 'ok'
  | 'not_ordered'
  | 'under'
  | 'over'
  | 'unit_mismatch'
  | 'without_need'
  | 'matched';

/** Доля строки ведомости в системе РД (разделы ОВ, ВК, НВ, НК; замечание ПТО
 *  №3): Σ долей = количество строки. «В1» ОВ2 и «В1» ВК1 — разные доли. */
export interface PositionSystemShare {
  key: string;
  /** «В1», «В25, В26» или «не определена». */
  label: string;
  code?: string | null;
  codes?: string[];
  /** Шифр комплекта и его марка («ОВ2»). */
  doc?: string;
  doc_mark?: string;
  quantity?: number | null;
  rows?: number;
  /** У строк доли есть сомнение разметки системы. */
  doubt?: boolean;
}

/** Кратность строки «на 1 …» по РД: сколько таких конструкций в документе. */
export interface PositionMultiplicity {
  /** `found` — число известно; `ambiguous` — в РД несколько чисел;
   *  `not_found` — применение не найдено; `no_element` — марка не определена. */
  status: 'found' | 'ambiguous' | 'not_found' | 'no_element';
  /** Марка элемента («БС-1»). */
  element?: string;
  /** Сборочная единица, чей состав на 1 шт («КП-1»). */
  assembly?: string;
  /** Число элементов по РД. */
  count?: number;
  /** Штук сборочной единицы на 1 элемент. */
  per_element?: number;
  /** Множитель количества строки. */
  factor?: number;
  /** Где в РД число: «стр. 10 (ЧТЖ05)». */
  source?: string;
  candidates?: string[];
}

/** Служебные поля позиции (`raw_data`). */
export interface PositionRaw {
  kind?: 'material' | 'rebar' | 'table';
  ord?: number;
  unit_mass?: number | null;
  total_weight?: number | null;
  rebar_bar?: boolean;
  per_unit?: boolean;
  sources?: string[];
  /** Разбивка по системам (только в разделах с системами). */
  by_system?: PositionSystemShare[];
  /** Кратность «на 1 …» по РД и количество / вес / масса с её учётом. */
  multiplicity?: PositionMultiplicity;
  /** Кратность, указанная снабженцем (важнее найденной по РД). */
  multiplicity_manual?: number;
  quantity_mult?: string;
  quantity_mult_num?: number;
  total_weight_mult?: number;
  mass_mult_kg?: number;
  /** Почему строка «проверить» — кроме кратности (она объяснена в
   *  `multiplicity`). */
  review_reasons?: string[];
  /** Строка ведомости объёмов работ — работа, а не материал. */
  vor_work?: boolean;
  /** Другие написания строки с тем же кодом продукции (сведены в одну). */
  name_variants?: string[];
  rebar_class?: string;
  diameter_mm?: number;
  count_pcs?: number | null;
  length_m?: number | null;
  mass_kg?: number | null;
  reconcile?: {
    status?: ReconcileStatus;
    need?: number | null;
    requested?: number | null;
    matched_name?: string | null;
    fuzzy?: boolean;
  };
}

export function positionRaw(p: Pick<MtoMaterialPosition, 'raw_data'>): PositionRaw {
  return isObj(p.raw_data) ? (p.raw_data as PositionRaw) : {};
}

/** Вид строки ведомости: перечень / свод арматуры / таблица Excel. */
export function positionKind(p: Pick<MtoMaterialPosition, 'raw_data'>): 'material' | 'rebar' | 'table' {
  const k = positionRaw(p).kind;
  return k === 'material' || k === 'rebar' ? k : 'table';
}

/** Строка требует проверки, и человек по ней ещё не решил (принятая и
 *  исключённая — уже проверены). */
export function needsCheck(p: Pick<MtoMaterialPosition, 'needs_review' | 'decision'>): boolean {
  return p.needs_review && !p.decision;
}

/** Кратность строки «на 1 …» для графы: «×16 · стр. 10 (ЧТЖ05)»; у состава
 *  сборки — «×16 (1 КП-1 на БС-1 × 16)». Не найдена — почему. */
export function multiplicityLabel(m: PositionMultiplicity): string {
  switch (m.status) {
    case 'found': {
      const factor = fmtNum(m.factor ?? m.count ?? null);
      const how =
        m.assembly && m.per_element != null
          ? ` (${fmtNum(m.per_element)} ${m.assembly} на ${m.element ?? 'элемент'} × ${fmtNum(m.count ?? null)})`
          : '';
      return `×${factor}${how}${m.source ? ` · ${m.source}` : ''}`;
    }
    case 'ambiguous':
      return 'кратность: в РД несколько чисел — укажите';
    case 'not_found':
      return 'кратность в РД не найдена — укажите';
    default:
      return 'кратность: марка элемента не определена — укажите';
  }
}

/** Количество из ведомости (текст «1234,5») — по-русски с разделителями;
 *  нечисловой текст («по проекту») — как есть. */
export function fmtQtyText(q: string | null | undefined): string {
  if (!q || !q.trim()) return '—';
  const n = Number(q.replace(/[\s\u00a0]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? fmtNum(n) : q;
}

export function reconcileStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'ok':
      return 'заказано полностью';
    case 'not_ordered':
      return 'не заказано';
    case 'under':
      return 'заказано меньше';
    case 'over':
      return 'заказано больше';
    case 'unit_mismatch':
      return 'разные единицы';
    case 'without_need':
      return 'без потребности';
    case 'matched':
      return 'в сверке';
    default:
      return '—';
  }
}

export function reconcileStatusTone(status: string | undefined): BadgeTone {
  switch (status) {
    case 'ok':
    case 'matched':
      return 'success';
    case 'not_ordered':
    case 'under':
      return 'danger';
    case 'over':
    case 'unit_mismatch':
    case 'without_need':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Откуда документ ведомости. */
export function docOriginLabel(origin: string): string {
  switch (origin) {
    case 'library':
      return 'библиотека РД';
    case 'library_file':
      return 'файл · найден в библиотеке';
    case 'tables':
      return 'файл · таблицы спецификаций';
    case 'spreadsheet':
      return 'таблица Excel';
    case 'ocr':
      return 'скан / фото';
    default:
      return origin;
  }
}

export function docStatusLabel(status: string): string {
  switch (status) {
    case 'ok':
      return 'в ведомости';
    case 'empty':
      return 'материалов нет';
    case 'processing':
      return 'ещё разбирается в ПТО';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

export function docStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'ok':
      return 'success';
    case 'empty':
      return 'warning';
    case 'processing':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Человеческое название вида замечания (вместо кода вроде `not_in_requests`). */
const ANOMALY_KIND_LABELS: Readonly<Record<string, string>> = {
  not_in_requests: 'Не заказано',
  under_request: 'Заказано меньше потребности',
  over_request: 'Заказано больше потребности',
  request_without_need: 'Заявлено без потребности',
  unit_mismatch: 'Разные единицы',
  duplicate_request: 'Дубли в заявках',
  rd_missing: 'Нет потребности по РД',
  requests_missing: 'Нет заявок',
  rd_document_processing: 'Документ ещё в разборе',
  rd_document_failed: 'Ошибка разбора документа',
  rd_document_gone: 'Документ удалён из библиотеки',
  rd_no_spec_tables: 'Нет таблиц спецификаций',
  ocr_truncated: 'Скан разобран не полностью',
  positions_need_review: 'Строки на проверку',
  extraction_empty: 'Ничего не извлечено',
  ocr_source: 'Распознано со скана',
  file_unreadable: 'Файл не прочитан',
  wrong_doc_type: 'Не тот тип документа',
  nsi_dictionary_empty: 'Справочник 1С пуст',
  nsi_no_auto: 'Нет автопривязок к 1С',
  nsi_variants: 'Разнопись наименований',
  batch_missing: 'Партия без протокола',
  protocol_missing: 'Протокол без партии',
  protocol_ambiguous: 'Несколько протоколов',
  quarry_mismatch: 'Другой карьер',
  overdue: 'Просрочка поставки',
  overdue_partial: 'Частичная просрочка',
  late_delivery: 'Поставка с опозданием',
  undersupply: 'Недопоставка',
  longlead_overdue: 'Точка заказа пройдена',
  longlead_due_soon: 'Скоро точка заказа',
  longlead_no_orderpoint: 'Не рассчитать точку заказа',
  illiquid: 'Неликвид',
  negative_stock: 'Отрицательный остаток',
};

export function anomalyKindLabel(kind: string): string {
  return ANOMALY_KIND_LABELS[kind] ?? '';
}

/** Число по-русски: до 3 знаков, без хвостовых нулей. `null` → «—». */
/** Доли систем строки ведомости: «ОВ2 · В1 — 5»; сомнение — «⚠». Без
 *  разбивки — пусто. */
export function systemShareLines(raw: PositionRaw): string[] {
  return (raw.by_system ?? []).map((s) => {
    const doc = s.doc_mark?.trim();
    const qty = s.quantity != null ? fmtNum(s.quantity) : '—';
    return `${doc ? `${doc} · ` : ''}${s.label} — ${qty}${s.doubt ? ' ⚠' : ''}`;
  });
}

export function fmtNum(v: number | null | undefined, maxDigits = 3): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: maxDigits });
}

/** Имя файла выгрузки: «Ведомость материалов — Базисный склад угля — 15.10.2026.xlsx». */
export function exportFileName(run: Pick<MtoRun, 'module' | 'created_at' | 'raw_data'>): string {
  const title: Record<string, string> = {
    rd_extract: 'Ведомость материалов',
    reconcile: 'Сверка с заявками',
    passports: 'Паспорта качества',
    supply: 'Контроль снабжения',
    long_lead: 'Длинноцикловые позиции',
    suppliers: 'Сводная таблица КП',
    stock: 'Остатки',
  };
  const head = title[run.module ?? ''] ?? 'МТО';
  const project = runRaw(run).project_name?.trim();
  const date = new Date(run.created_at);
  const day = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('ru-RU');
  const parts = [head, project, day].filter(Boolean).join(' — ');
  // Символы, недопустимые в именах файлов Windows.
  return `${parts.replace(/[\\/:*?"<>|]/g, ' ').slice(0, 150)}.xlsx`;
}

/** Сохранить Blob как файл. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
